/**
 * One cloud run, now (E14): "Send the next one" for a person on Auto who
 * has a minute and wants to see it work, and "Rehearse the next one" for
 * anyone who wants to see what Auto would do before trusting it. The run
 * happens in a browser Cloudflare hosts; this watches it and says what
 * happened, in the runner's own words.
 */

import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "./auth";

export interface RunStatus {
  id: string;
  status: string;
  output: { outcome?: string; jobId?: string; title?: string; employer?: string; summary?: string; note?: string; dryRun?: boolean } | null;
  error: unknown;
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

export function runWords(r: RunStatus): string {
  const o = r.output;
  if (r.status === "errored" || r.status === "terminated") {
    const e = r.error as { message?: string } | string | null;
    return `The run did not finish: ${typeof e === "string" ? e : e?.message ?? "the browser step failed."}`;
  }
  if (!o) return "Working…";
  if (o.outcome === "nothing") return o.note ?? "Nothing in the queue right now.";
  const who = o.title && o.employer ? `${o.title} at ${o.employer}: ` : "";
  switch (o.outcome) {
    case "submitted": return `${who}sent. The record is under Applications.`;
    case "filled": return `${who}${o.summary ?? "rehearsed."}`;
    case "blocked": return `${who}left for you — ${o.summary ?? "a question it could not answer."} See Applications.`;
    case "failed": return `${who}did not go through — ${o.summary ?? "the fill failed."}`;
    case "unknown": return `${who}Submit was pressed and the page did not confirm. Check your email.`;
    case "no-form": return `${who}no application form was found on the page.`;
    default: return `${who}${o.summary ?? o.outcome ?? "done."}`;
  }
}

/**
 * One cloud run at a time, watched until it is over (or eight minutes pass).
 * Shared by the dashboard's buttons and the Applications row's "Send it now".
 */
export function useCloudRun(onOver?: (r: RunStatus) => void) {
  const [run, setRun] = useState<RunStatus | null>(null);
  const [busy, setBusy] = useState<"send" | "rehearse" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const over = useRef(onOver);
  over.current = onOver;

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const watch = (id: string, startedAt: number) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api<RunStatus>(`/api/auto/runs/${id}`);
        setRun(r);
        const done = r.status === "complete" || r.status === "errored" || r.status === "terminated";
        if (!done && Date.now() - startedAt < 8 * 60_000) watch(id, startedAt);
        else { setBusy(null); if (done) over.current?.(r); }
      } catch (e) {
        setError(describe(e));
        setBusy(null);
      }
    }, 5_000);
  };

  const start = async (dryRun: boolean, jobId?: string) => {
    setBusy(dryRun ? "rehearse" : "send");
    setError(null);
    setRun(null);
    try {
      const r = await api<{ id: string }>("/api/auto/runs", { method: "POST", body: JSON.stringify(jobId ? { dryRun, jobId } : { dryRun }) });
      setRun({ id: r.id, status: "queued", output: null, error: null });
      watch(r.id, Date.now());
    } catch (e) {
      setError(describe(e));
      setBusy(null);
    }
  };

  /** What to show under the buttons, if anything. */
  const line = run
    ? { text: busy && !run.output ? "A browser in the cloud is opening the form… this takes a minute or two." : runWords(run), ok: run.output?.outcome === "submitted" }
    : null;

  return { run, busy, error, start, line };
}

/**
 * A real cloud send behind one deliberate confirm, so it is never a stray
 * click. "Send" here always means a real application: the first press asks,
 * the second sends. Rehearsing is a separate, clearly-labelled action, never
 * hidden behind the same button.
 */
export function CloudSendButton({ onSend, busy, disabled, idle, note, className = "btn ghost small" }: {
  onSend: () => void;
  busy: boolean;
  disabled?: boolean;
  idle: string;
  note: string;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  if (confirming && !busy) {
    return (
      <span className="send-confirm">
        <span className="small">{note}</span>
        <button className="btn small" type="button" disabled={disabled} onClick={() => { setConfirming(false); onSend(); }}>Send application</button>
        <button className="step-link small" type="button" onClick={() => setConfirming(false)}>Cancel</button>
      </span>
    );
  }
  return (
    <button className={className} type="button" disabled={busy || disabled} onClick={() => setConfirming(true)}>
      {busy ? "Sending…" : idle}
    </button>
  );
}

export function AutoRun({ mode, onReview }: { mode: "auto" | "review"; onReview?: (jobId: string) => void }) {
  const { run, busy, error, start, line } = useCloudRun();
  // Once a run is over and named a posting, the Applications row that says
  // exactly what was filled and what was left is one tap away -- "See
  // Applications" used to drop the person at the top of the page to hunt.
  const reviewable = run && busy === null && run.output?.jobId && run.output.outcome !== "nothing" ? run.output.jobId : null;

  return (
    <div className="auto-run">
      <div className="row">
        {mode === "auto" && (
          <CloudSendButton
            onSend={() => void start(false)}
            busy={busy === "send"}
            disabled={busy !== null}
            idle="Send the next one now"
            note="This sends a real application to your next matching job — not a rehearsal."
          />
        )}
        <button className="step-link small" disabled={busy !== null} onClick={() => void start(true)}>
          {busy === "rehearse" ? "Rehearsing…" : mode === "auto" ? "Rehearse the next one · nothing is sent" : "Rehearse what Auto would send · nothing is sent"}
        </button>
      </div>
      {line && <p className={`small ${line.ok ? "ok" : "muted"}`}>{line.text}</p>}
      {reviewable && onReview && (
        <p className="small"><button className="btn ghost small" onClick={() => onReview(reviewable)}>Review this application →</button></p>
      )}
      {error && <p className="err small">{error}</p>}
    </div>
  );
}
